// Faithful CMS booking preview: the switcher rewrites ?tab= / ?property= on
// the render-preview URL held in $store.cmsPreviewURL (the iframe :src and the
// in-place fragment refresh read it / iframe.src), so the chosen state survives
// edits. Only these two params are writable from the switcher.
export const BOOKING_PREVIEW_PARAMS = ["tab", "property"] as const;
export type BookingPreviewParam = (typeof BOOKING_PREVIEW_PARAMS)[number];

const BASE = "http://cms.invalid";

export function isBookingPreviewParam(name: string): name is BookingPreviewParam {
  return (BOOKING_PREVIEW_PARAMS as readonly string[]).includes(name);
}

function parse(url: string): URL {
  return new URL(url || "/", BASE);
}

function serialize(u: URL, original: string): string {
  return /^https?:\/\//.test(original) ? u.toString() : u.pathname + u.search + u.hash;
}

export function readBookingPreviewParam(url: string, name: BookingPreviewParam): string {
  return parse(url).searchParams.get(name) ?? "";
}

export function withBookingPreviewParam(url: string, name: BookingPreviewParam, value: string): string {
  const u = parse(url);
  u.searchParams.delete("_t");
  if (value) u.searchParams.set(name, value);
  else u.searchParams.delete(name);
  return serialize(u, url);
}

/** Section-mode rail nav swaps in the section's own URL (which picks its
 *  tab); keep the operator's chosen accommodation across that swap. */
export function carryBookingPreviewProperty(prevURL: string, nextURL: string): string {
  const next = parse(nextURL);
  if (next.searchParams.get("context") !== "booking" || next.searchParams.has("property")) return nextURL;
  const prev = parse(prevURL);
  const property = prev.searchParams.get("context") === "booking" ? prev.searchParams.get("property") : null;
  if (!property) return nextURL;
  next.searchParams.set("property", property);
  return serialize(next, nextURL);
}

/** Editor re-init (a body swap re-runs init() with the server's baked
 *  previewURL): on the SAME booking preview page (same path, same section)
 *  keep the operator's chosen tab and property. A different section keeps
 *  its own tab (the section picks its state) and only the property carries;
 *  other contexts/websites are left untouched. The booking editor previews
 *  the full page (no section= in the URL), so callers pass the editor
 *  sections the two URLs were built for; without them the URLs' section=
 *  params decide. */
export function carryBookingPreviewState(
  prevURL: string,
  nextURL: string,
  prevSection?: string,
  nextSection?: string,
): string {
  if (!prevURL) return nextURL;
  const prev = parse(prevURL);
  const next = parse(nextURL);
  if (prev.pathname !== next.pathname) return nextURL;
  const carried = carryBookingPreviewProperty(prevURL, nextURL);
  if (prev.searchParams.get("context") !== "booking" || next.searchParams.get("context") !== "booking") return carried;
  if ((prev.searchParams.get("section") ?? "") !== (next.searchParams.get("section") ?? "")) return carried;
  if (prevSection !== undefined && nextSection !== undefined && prevSection !== nextSection) return carried;
  const tab = prev.searchParams.get("tab");
  if (!tab || tab === next.searchParams.get("tab")) return carried;
  return withBookingPreviewParam(carried, "tab", tab);
}
